import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, Receipt, PieChart, Target, Settings, LogOut, CreditCard, Zap, MoreHorizontal, Plus } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import SpiderLogo from "./SpiderLogo";
import ThemeToggle from "./ThemeToggle";
import { useNavigate } from "react-router-dom";

const items = [
  { to: "/dashboard", label: "Home", fullLabel: "Dashboard", icon: Home, testid: "nav-dashboard" },
  { to: "/transactions", label: "Tx", fullLabel: "Transactions", icon: Receipt, testid: "nav-transactions" },
  { to: "/analytics", label: "Stats", fullLabel: "Analytics", icon: PieChart, testid: "nav-analytics" },
  { to: "/debts", label: "Debts", fullLabel: "Debts", icon: CreditCard, testid: "nav-debts" },
  { to: "/bills", label: "Bills", fullLabel: "Bills", icon: Zap, testid: "nav-bills" },
  { to: "/goals", label: "Goals", fullLabel: "Goals", icon: Target, testid: "nav-goals" },
  { to: "/settings", label: "More", fullLabel: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Shell({ children }) {
  const { user, logout, setUser } = useAuth();
  const location = useLocation();

  const changeCurrency = async (v) => {
    try {
      const r = await api.patch("/user/settings", { currency: v });
      setUser(r.data);
    } catch (e) { console.warn("currency update failed", e); }
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC] dark:bg-[#0B1220]">
      {/* Desktop sidebar */}
      <aside className="w-64 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hidden md:flex flex-col">
        <div className="px-6 py-6 flex items-center gap-2">
          <div className="w-10 h-10 rounded-2xl bg-spidey-red grid place-items-center text-white">
            <SpiderLogo className="w-6 h-6" color="#ffffff" />
          </div>
          <span className="font-display text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">Argent</span>
        </div>
        <nav className="px-3 flex-1">
          {items.map((it) => {
            const active = location.pathname.startsWith(it.to);
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium mb-1 transition-colors duration-200 ${active ? "bg-spidey-red text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"}`}
              >
                <it.icon className="w-4 h-4" />
                {it.fullLabel}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-sky-100 dark:bg-sky-900/50 grid place-items-center text-sky-700 dark:text-sky-300 font-bold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{user?.name || "You"}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 pb-24 md:pb-0">
        {/* Mobile top bar (brand + theme + currency) */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-spidey-red grid place-items-center">
              <SpiderLogo className="w-5 h-5" color="#ffffff" />
            </div>
            <span className="font-display text-base font-bold text-slate-900 dark:text-slate-100">Argent</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle size="sm" testId="theme-toggle-btn-mobile" />
            <Select value={user?.currency || "USD"} onValueChange={changeCurrency}>
              <SelectTrigger data-testid="currency-selector-mobile" className="w-[90px] h-9 rounded-full border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["USD", "EUR", "INR", "GBP", "JPY"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Desktop header */}
        <header className="hidden md:flex items-center justify-between px-6 lg:px-10 py-6 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div>
            <div className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Hi, {user?.name?.split(" ")[0] || "there"}</div>
            <div className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5">Welcome back</div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle testId="theme-toggle-btn" />
            <Select value={user?.currency || "USD"} onValueChange={changeCurrency}>
              <SelectTrigger data-testid="currency-selector" className="w-[110px] rounded-full border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["USD", "EUR", "INR", "GBP", "JPY"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </header>

        <div className="px-4 md:px-6 lg:px-10 py-6 md:py-8">{children}</div>
      </main>

      {/* Mobile FAB — "+" add transaction, sits above the bottom tab bar */}
      <MobileFab />

      {/* Mobile bottom tab bar — 5 primary tabs + a "More" sheet for the rest */}
      <MobileNav />
    </div>
  );
}

// Floating "+" action button — only on phones. Navigates to /transactions?add=1
function MobileFab() {
  const location = useLocation();
  const navigate = useNavigate();
  // Hide on Settings + Login to avoid confusion
  if (location.pathname.startsWith("/settings")) return null;
  return (
    <button
      data-testid="mobile-fab-add-tx"
      onClick={() => navigate("/transactions?add=1")}
      aria-label="Add transaction"
      className="md:hidden fixed right-4 z-50 w-14 h-14 rounded-full bg-spidey-red text-white shadow-lg shadow-black/30 grid place-items-center hover:scale-105 active:scale-95 transition-transform"
      style={{
        bottom: "calc(env(safe-area-inset-bottom) + 74px)",
      }}
    >
      <Plus className="w-6 h-6" strokeWidth={2.5} />
    </button>
  );
}

// Mobile bottom nav: 5 primary tabs visible, everything else lives inside a "More" sheet.
function MobileNav() {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = React.useState(false);
  const primary = items.slice(0, 5); // Home, Tx, Analytics, Debts, Bills
  const extras = items.slice(5);      // Goals, Settings

  return (
    <>
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-6">
          {primary.map((it) => {
            const active = location.pathname.startsWith(it.to);
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`${it.testid}-mobile`}
                className={`flex flex-col items-center justify-center gap-1 py-2 text-[10px] font-semibold transition-colors min-h-[56px] ${active ? "text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400"}`}
              >
                <div className={`w-8 h-8 rounded-2xl grid place-items-center transition-colors ${active ? "bg-spidey-red text-white" : "text-slate-500 dark:text-slate-400"}`}>
                  <it.icon className="w-[18px] h-[18px]" />
                </div>
                <span>{it.label}</span>
              </NavLink>
            );
          })}
          <button
            type="button"
            data-testid="nav-more-mobile"
            onClick={() => setMoreOpen(v => !v)}
            className={`flex flex-col items-center justify-center gap-1 py-2 text-[10px] font-semibold transition-colors min-h-[56px] ${moreOpen ? "text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400"}`}
          >
            <div className={`w-8 h-8 rounded-2xl grid place-items-center transition-colors ${moreOpen ? "bg-spidey-red text-white" : "text-slate-500 dark:text-slate-400"}`}>
              <MoreHorizontal className="w-[18px] h-[18px]" />
            </div>
            <span>More</span>
          </button>
        </div>
      </nav>

      {/* More sheet — appears above the tab bar */}
      {moreOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/40"
            onClick={() => setMoreOpen(false)}
            aria-hidden="true"
          />
          <div
            data-testid="mobile-more-sheet"
            className="md:hidden fixed inset-x-0 z-50 rounded-t-3xl bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shadow-xl animate-in slide-in-from-bottom duration-200"
            style={{
              bottom: "calc(env(safe-area-inset-bottom) + 60px)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <div className="pt-3 pb-2">
              <div className="mx-auto w-10 h-1 bg-slate-200 dark:bg-slate-700 rounded-full" />
            </div>
            <div className="px-4 pb-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400 mb-2">More</div>
              {extras.map(it => {
                const active = location.pathname.startsWith(it.to);
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onClick={() => setMoreOpen(false)}
                    data-testid={`${it.testid}-mobile-more`}
                    className={`flex items-center gap-3 px-3 py-3 rounded-2xl mb-1 text-sm font-semibold ${active ? "bg-spidey-red text-white" : "text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                  >
                    <it.icon className="w-5 h-5" />
                    {it.fullLabel}
                  </NavLink>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
